/**
 * LoginForm - Organism component
 * Complete login form with validation
 * WCAG 2.1 AA compliant
 */

import React, { useState } from 'react';
import { FormField, Alert, Input } from '@kisanlink/ui-commons';
import { Button } from '@/components/atoms';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

export interface LoginFormProps {
  /**
   * Callback when login is successful
   */
  onSuccess?: () => void;
  /**
   * Show "Remember me" checkbox
   */
  showRememberMe?: boolean;
  /**
   * Show "Forgot password" link
   */
  showForgotPassword?: boolean;
  /**
   * Callback when "Forgot password" is clicked
   */
  onForgotPassword?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  showRememberMe = false,
  showForgotPassword = false,
  onForgotPassword,
}) => {
  const navigate = useNavigate();
  const { login, error: apiError } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    phoneNumber?: string;
    password?: string;
  }>({});

  const validate = () => {
    const errors: { phoneNumber?: string; password?: string } = {};

    if (!phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10}$/.test(phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsLoading(true);
      await login({
        country_code: countryCode,
        phone_number: phoneNumber,
        password,
      });

      // Success - navigate or callback
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/');
      }
    } catch (err) {
      // Error is handled by authSlice
      console.error('Login failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const errorMessage = apiError || null;

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md space-y-6" noValidate>
      {errorMessage && (
        <Alert variant="error" onClose={() => {}}>
          {errorMessage}
        </Alert>
      )}

      <div className="space-y-2">
        <label htmlFor="phone" className="block text-sm font-medium text-neutral-700">
          Phone Number
          <span className="text-error-600 ml-1" aria-label="required">*</span>
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            disabled={isLoading}
            className="w-20 px-3 py-2 rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-100"
            placeholder="+91"
          />
          <FormField error={validationErrors.phoneNumber}>
            <Input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              autoComplete="tel"
              disabled={isLoading}
              placeholder="1234567890"
            />
          </FormField>
        </div>
      </div>

      <FormField
        label="Password"
        error={validationErrors.password}
        required
      >
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          disabled={isLoading}
          placeholder="Enter your password"
        />
      </FormField>

      {showRememberMe && (
        <div className="flex items-center">
          <input
            id="remember-me"
            name="remember-me"
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            disabled={isLoading}
            className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          <label
            htmlFor="remember-me"
            className="ml-2 block text-sm text-neutral-900"
          >
            Remember me
          </label>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isLoading}
          disabled={isLoading}
          className="w-full"
        >
          Sign in
        </Button>
      </div>

      {showForgotPassword && (
        <div className="text-center">
          <button
            type="button"
            onClick={onForgotPassword}
            disabled={isLoading}
            className="text-sm font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Forgot your password?
          </button>
        </div>
      )}
    </form>
  );
};
